import { PrivacyExternalMediaElement } from './external-media';
import {
  applyPolicyConstraints,
  createState,
  DEFAULT_COOKIE_NAME,
  defaultPreferences,
  deleteCookie,
  getGpcState,
  getEffectiveMode,
  readConsentCookieResult,
  validateConfig,
  writeConsentCookie,
} from './store';
import type {
  ConsentChangeDetail,
  ConsentConfig,
  ConsentEventName,
  ConsentManagerApi,
  ConsentSource,
  ConsentState,
} from './types';
import { PrivacyConsentCenterElement } from './ui';

export * from './types';
export { applyPolicyConstraints, createState, defaultPreferences, getEffectiveMode, readConsentCookieResult, validateConfig } from './store';

const CENTER_TAG = 'privacy-consent-center';
const MEDIA_TAG = 'privacy-external-media';

interface ManagedScript {
  categoryId: string;
  preload: HTMLLinkElement | null;
  element: HTMLScriptElement | null;
  promise: Promise<HTMLScriptElement>;
  cancelled: boolean;
  settled: boolean;
  cancel: (reason: Error) => void;
}

class ConsentManager implements ConsentManagerApi {
  private state: ConsentState | null;
  private center: PrivacyConsentCenterElement | null = null;
  private scripts = new Map<string, ManagedScript>();
  private disabledVendors = new Set<string>();
  private invalidStoredConsent: boolean;
  private consentRevision = 0;
  private operationQueue: Promise<void> = Promise.resolve();

  constructor(private readonly config: ConsentConfig) {
    validateConfig(config);
    const storedConsent = readConsentCookieResult(config);
    this.state = storedConsent.state;
    this.invalidStoredConsent = storedConsent.status === 'invalid';
    if (this.invalidStoredConsent) deleteCookie(config.cookieName ?? DEFAULT_COOKIE_NAME);

    const gpc = getGpcState();
    if (this.state && gpc) {
      this.state = {
        ...this.state,
        gpc: true,
        categories: applyPolicyConstraints(config, this.state.categories, true),
      };
      writeConsentCookie(config, this.state);
    }

    this.setGoogleConsentDefaults();
    this.bindDocumentActions();
    this.initializeUi();
    this.deleteDisabledVendorCookies();
    const initialization = this.queueConsentApplication(this.consentRevision);
    void initialization.catch((error: unknown) => {
      console.error('[PrivacyConsent] 初期停止処理に失敗しました', error);
    }).finally(() => {
      if (gpc) this.emit('consent:gpc-detected', { state: this.state, previousState: null });
      this.emit('consent:ready', { state: this.state, previousState: null });
    });
  }

  hasConsent(categoryId: string): boolean {
    const category = this.config.categories.find((item) => item.id === categoryId);
    if (!category) return false;
    if (category.required) return true;
    if (this.invalidStoredConsent) return false;
    if (!this.state) return getEffectiveMode(this.config) === 'notice-with-optout' && !(getGpcState() && category.gpcOptOut);
    if (getGpcState() && category.gpcOptOut) return false;
    return Boolean(this.state.categories[categoryId]);
  }

  getState(): ConsentState | null {
    return this.state ? structuredClone(this.state) : null;
  }

  openPreferences(): void {
    this.center?.openPreferences();
  }

  acceptAll(): Promise<void> {
    const preferences = Object.fromEntries(this.config.categories.map((category) => [category.id, true]));
    return this.updatePreferences(preferences, 'banner');
  }

  rejectOptional(): Promise<void> {
    return this.updatePreferences(defaultPreferences({ ...this.config, mode: 'global-strict' }), 'banner');
  }

  updatePreferences(preferences: Record<string, boolean>, source: ConsentSource = 'preferences'): Promise<void> {
    return this.commitPreferences(preferences, source, false);
  }

  withdrawAll(): Promise<void> {
    return this.commitPreferences(defaultPreferences({ ...this.config, mode: 'global-strict' }), 'preferences', true);
  }

  private async commitPreferences(preferences: Record<string, boolean>, source: ConsentSource, withdrawn: boolean): Promise<void> {
    const previousState = this.state;
    this.state = createState(this.config, preferences, getGpcState() ? 'gpc' : source);
    this.invalidStoredConsent = false;
    const revision = ++this.consentRevision;
    writeConsentCookie(this.config, this.state);
    this.updateGoogleConsentMode();
    this.cancelDisallowedScripts();
    this.updateDeclarativeFrames();
    this.deleteDisabledVendorCookies();
    let applicationFailed = false;
    let applicationError: unknown;
    try {
      await this.queueConsentApplication(revision);
    } catch (error) {
      applicationFailed = true;
      applicationError = error;
    }

    if (revision !== this.consentRevision || !this.state) {
      if (applicationFailed) throw applicationError;
      return;
    }

    this.center?.setState(this.state);
    if (!applicationFailed) {
      if (withdrawn) {
        this.emit('consent:withdrawn', { state: this.state, previousState });
      } else {
        const optionalGranted = this.config.categories.some((category) => !category.required && this.state?.categories[category.id]);
        this.emit(optionalGranted ? 'consent:granted' : 'consent:denied', { state: this.state, previousState });
      }
    }
    this.emit('consent:changed', { state: this.state, previousState });

    if (applicationFailed) {
      this.center?.showError();
      throw applicationError;
    }
    this.center?.showToast();
  }

  loadScript(categoryId: string, id: string, src: string, attributes: Record<string, string> = {}): Promise<HTMLScriptElement> {
    return this.operationQueue.then(() => this.loadScriptNow(categoryId, id, src, attributes));
  }

  private loadScriptNow(categoryId: string, id: string, src: string, attributes: Record<string, string> = {}): Promise<HTMLScriptElement> {
    if (!this.config.categories.some((category) => category.id === categoryId)) {
      return Promise.reject(new Error(`カテゴリが存在しません: ${categoryId}`));
    }
    if (!id) return Promise.reject(new Error('スクリプトIDは必須です'));
    if (!this.hasConsent(categoryId)) {
      return Promise.reject(new Error(`カテゴリ ${categoryId} は許可されていません`));
    }

    const pending = this.scripts.get(id);
    if (pending) {
      if (pending.categoryId !== categoryId) {
        return Promise.reject(new Error(`スクリプトID ${id} は別のカテゴリで使用されています`));
      }
      return pending.promise;
    }

    const existing = document.getElementById(id);
    if (existing instanceof HTMLScriptElement) {
      if (existing.type === 'text/plain' && existing.dataset.consentCategory) {
        return Promise.reject(new Error(`スクリプトID ${id} は同意待ち要素で使用されています`));
      }
      const promise = Promise.resolve(existing);
      this.scripts.set(id, {
        categoryId,
        preload: null,
        element: existing,
        promise,
        cancelled: false,
        settled: true,
        cancel: () => existing.remove(),
      });
      return promise;
    }

    let url: URL;
    try {
      url = new URL(src, location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) throw new Error('許可されていないURLです');
    } catch (error) {
      return Promise.reject(error);
    }

    let resolveLoad!: (script: HTMLScriptElement) => void;
    let rejectLoad!: (reason: Error) => void;
    const promise = new Promise<HTMLScriptElement>((resolve, reject) => {
      resolveLoad = resolve;
      rejectLoad = reject;
    });
    const preload = document.createElement('link');
    const managed: ManagedScript = {
      categoryId,
      preload,
      element: null,
      promise,
      cancelled: false,
      settled: false,
      cancel: (reason: Error) => {
        if (managed.cancelled) return;
        managed.cancelled = true;
        managed.preload?.remove();
        managed.element?.remove();
        if (!managed.settled) rejectLoad(reason);
      },
    };
    this.scripts.set(id, managed);

    const fail = (error: Error): void => {
      if (managed.settled) return;
      managed.settled = true;
      managed.preload?.remove();
      managed.element?.remove();
      if (this.scripts.get(id) === managed) this.scripts.delete(id);
      rejectLoad(error);
    };

    preload.rel = 'preload';
    preload.as = 'script';
    preload.href = url.href;
    if (this.config.nonce) preload.nonce = this.config.nonce;
    const crossOrigin = attributes.crossorigin ?? attributes.crossOrigin;
    if (crossOrigin !== undefined) preload.crossOrigin = crossOrigin;
    if (attributes.integrity) preload.integrity = attributes.integrity;
    if (attributes.referrerpolicy) preload.referrerPolicy = attributes.referrerpolicy;
    preload.addEventListener('error', () => fail(new Error(`${url.href} の事前読み込みに失敗しました`)), { once: true });
    preload.addEventListener('load', () => {
      preload.remove();
      managed.preload = null;
      if (managed.cancelled || !this.hasConsent(categoryId)) {
        managed.cancel(new Error(`カテゴリ ${categoryId} の同意が撤回されました`));
        if (this.scripts.get(id) === managed) this.scripts.delete(id);
        return;
      }

      const script = document.createElement('script');
      managed.element = script;
      script.id = id;
      script.src = url.href;
      script.async = true;
      if (this.config.nonce) script.nonce = this.config.nonce;
      for (const [name, value] of Object.entries(attributes)) {
        if (!['src', 'id'].includes(name.toLowerCase())) script.setAttribute(name, value);
      }
      script.addEventListener('load', () => {
        if (managed.cancelled || !this.hasConsent(categoryId)) {
          script.remove();
          this.queueLateVendorCleanup(categoryId);
          return;
        }
        managed.settled = true;
        resolveLoad(script);
      }, { once: true });
      script.addEventListener('error', () => fail(new Error(`${url.href} の読み込みに失敗しました`)), { once: true });
      document.head.append(script);
    }, { once: true });
    document.head.append(preload);
    return promise;
  }

  on(event: ConsentEventName, listener: EventListener): () => void {
    window.addEventListener(event, listener);
    return () => window.removeEventListener(event, listener);
  }

  private initializeUi(): void {
    const mount = (): void => {
      this.center = document.querySelector<PrivacyConsentCenterElement>(CENTER_TAG) ?? document.createElement(CENTER_TAG) as PrivacyConsentCenterElement;
      if (!this.center.isConnected) document.body.append(this.center);
      this.center.initialize(this.config, this, this.state);
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
    else mount();
  }

  private bindDocumentActions(): void {
    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target.closest('[data-privacy-settings]') : null;
      if (!target) return;
      event.preventDefault();
      this.openPreferences();
    });
  }

  private queueConsentApplication(revision: number): Promise<void> {
    const operation = this.operationQueue
      .catch(() => undefined)
      .then(() => this.applyConsent(revision));
    this.operationQueue = operation.catch(() => undefined);
    return operation;
  }

  private async applyConsent(revision: number): Promise<void> {
    if (revision !== this.consentRevision) return;
    this.updateGoogleConsentMode();
    this.cancelDisallowedScripts();
    this.deleteDisabledVendorCookies();
    await this.disableDisallowedVendors();
    if (revision !== this.consentRevision) return;
    this.activateDeclarativeScripts();
    this.updateDeclarativeFrames();
  }

  private activateDeclarativeScripts(): void {
    document.querySelectorAll<HTMLScriptElement>('script[type="text/plain"][data-consent-category][data-consent-src]').forEach((blocked) => {
      const category = blocked.dataset.consentCategory ?? '';
      if (!this.hasConsent(category) || blocked.dataset.consentLoaded === 'true') return;
      blocked.dataset.consentLoaded = 'true';
      const src = blocked.dataset.consentSrc;
      if (!src) return;
      const id = blocked.dataset.consentRuntimeId
        ?? (blocked.id ? `${blocked.id}--privacy-loaded` : `privacy-script-${Math.random().toString(36).slice(2)}`);
      blocked.dataset.consentRuntimeId = id;
      const attributes = Object.fromEntries(
        Array.from(blocked.attributes)
          .filter((attribute) => ![
            'type',
            'src',
            'id',
            'data-consent-category',
            'data-consent-src',
            'data-consent-loaded',
            'data-consent-runtime-id',
          ].includes(attribute.name))
          .map((attribute) => [attribute.name, attribute.value]),
      );
      void this.loadScriptNow(category, id, src, attributes).catch(() => {
        delete blocked.dataset.consentLoaded;
      });
    });
  }

  private cancelDisallowedScripts(): void {
    for (const [id, managed] of this.scripts) {
      if (this.hasConsent(managed.categoryId)) continue;
      managed.cancel(new Error(`カテゴリ ${managed.categoryId} の同意が撤回されました`));
      this.scripts.delete(id);
    }

    document.querySelectorAll<HTMLScriptElement>('script[type="text/plain"][data-consent-category][data-consent-loaded]').forEach((blocked) => {
      if (!this.hasConsent(blocked.dataset.consentCategory ?? '')) delete blocked.dataset.consentLoaded;
    });
  }

  private async disableVendor(vendor: ConsentConfig['vendors'][number], force = false): Promise<void> {
    if (this.hasConsent(vendor.categoryId)) {
      this.disabledVendors.delete(vendor.id);
      return;
    }
    if (!force && this.disabledVendors.has(vendor.id)) return;
    try {
      await vendor.onDisable?.();
      if (!this.hasConsent(vendor.categoryId)) this.disabledVendors.add(vendor.id);
    } catch (error) {
      this.disabledVendors.delete(vendor.id);
      console.error(`[PrivacyConsent] ${vendor.id} の停止処理に失敗しました`, error);
      throw error;
    }
  }

  private async disableVendors(vendors: ConsentConfig['vendors'], force = false): Promise<void> {
    const results = await Promise.allSettled(vendors.map((vendor) => this.disableVendor(vendor, force)));
    const failures = results
      .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
      .map((result) => result.reason);
    if (failures.length === 1) throw failures[0];
    if (failures.length > 1) throw new AggregateError(failures, `${failures.length}件のベンダー停止処理に失敗しました`);
  }

  private async disableDisallowedVendors(): Promise<void> {
    await this.disableVendors(this.config.vendors);
  }

  private queueLateVendorCleanup(categoryId: string): void {
    const cleanup = this.operationQueue.catch(() => undefined).then(async () => {
      if (this.hasConsent(categoryId)) return;
      const vendors = this.config.vendors.filter((vendor) => vendor.categoryId === categoryId);
      await this.disableVendors(vendors, true);
    });
    this.operationQueue = cleanup.catch((error: unknown) => {
      console.error(`[PrivacyConsent] ${categoryId} の遅延停止処理に失敗しました`, error);
    });
  }

  private updateDeclarativeFrames(): void {
    document.querySelectorAll<HTMLElement>('[data-consent-category][data-consent-src]:not(script)').forEach((element) => {
      const category = element.dataset.consentCategory ?? '';
      if (this.hasConsent(category)) {
        if (!element.getAttribute('src')) element.setAttribute('src', element.dataset.consentSrc ?? '');
      } else {
        element.removeAttribute('src');
      }
    });
  }

  private deleteDisabledVendorCookies(): void {
    for (const vendor of this.config.vendors) {
      if (this.hasConsent(vendor.categoryId)) continue;
      for (const cookie of vendor.cookies ?? []) deleteCookie(cookie.name, cookie.path, cookie.domain);
      try {
        for (const key of vendor.localStorageKeys ?? []) localStorage.removeItem(key);
        for (const key of vendor.sessionStorageKeys ?? []) sessionStorage.removeItem(key);
      } catch {
        // ブラウザ設定でStorageへのアクセスが拒否されている場合も同意状態の更新は継続する。
      }
    }
  }

  private setGoogleConsentDefaults(): void {
    if (!this.config.googleConsentMode?.enabled) return;
    window.dataLayer = window.dataLayer ?? [];
    window.gtag = window.gtag ?? function (...args: unknown[]) { window.dataLayer?.push(args); };
    const defaults = this.googleConsentValues(false);
    window.gtag('consent', 'default', defaults);
  }

  private updateGoogleConsentMode(): void {
    if (!this.config.googleConsentMode?.enabled || !window.gtag) return;
    window.gtag('consent', 'update', this.googleConsentValues(true));
  }

  private googleConsentValues(useCurrent: boolean): Record<string, 'granted' | 'denied'> {
    const mapping = this.config.googleConsentMode?.categoryMapping ?? {};
    const types = ['analytics_storage', 'ad_storage', 'ad_user_data', 'ad_personalization', 'functionality_storage', 'personalization_storage', 'security_storage'] as const;
    return Object.fromEntries(types.map((type) => {
      const category = mapping[type];
      const granted = category ? (useCurrent ? this.hasConsent(category) : this.config.categories.find((item) => item.id === category)?.required === true) : false;
      return [type, granted ? 'granted' : 'denied'];
    })) as Record<string, 'granted' | 'denied'>;
  }

  private emit(name: ConsentEventName, detail: { state: ConsentState | null; previousState: ConsentState | null }): void {
    const snapshot = structuredClone(detail);
    window.dispatchEvent(new CustomEvent<ConsentChangeDetail>(name, { detail: snapshot }));
  }
}

let instance: ConsentManagerApi | null = null;

export const init = (config: ConsentConfig): ConsentManagerApi => {
  if (instance) return instance;
  try {
    if (!customElements.get(CENTER_TAG)) customElements.define(CENTER_TAG, PrivacyConsentCenterElement);
    if (!customElements.get(MEDIA_TAG)) customElements.define(MEDIA_TAG, PrivacyExternalMediaElement);
    instance = new ConsentManager(config);
    window.privacyConsent = instance;
    return instance;
  } catch (error) {
    window.dispatchEvent(new CustomEvent('consent:config-error', { detail: error }));
    throw error;
  }
};

export const getInstance = (): ConsentManagerApi | null => instance;

if (typeof window !== 'undefined') {
  window.PrivacyConsent = { init, getInstance };
}
